<template>
  <div class="readiness-container-new-readiness">
    <ReadinessPage
      :menuItems="menuItems"
      :initialTab="initialTab"
      @tab-selected="selectTab"
      :averageReadiness="averageReadiness"
      :last_month_averageReadiness="last_month_averageReadiness"
      :positionsAverageReadiness="positionsAverageReadiness"
      :employees="employees"
      :filterGroups="filterGroups"
      :appliedFilters="appliedFiltersAsLabels"
      :searchText="searchText"
      :totalCount="totalCount"
      :currentPage="currentPage"
      :itemsPerPage="itemsPerPage"
      :toggleLoader="readinessLoading"
      :employeesLoading="employeesLoading"
      :recalculating="recalculating"
      :positions="positions"
      :positionsFilterGroups="positionsFilterGroups"
      :positionsAppliedFilters="positionsAppliedFilters"
      :positionsSearchText="positionsSearchText"
      :positionsTotalCount="positionsTotalCount"
      :positionsCurrentPage="positionsCurrentPage"
      :positionsItemsPerPage="positionsItemsPerPage"
      :positionsToggleLoader="positionsLoading"
      :departments="departments"
      :departmentLoading="departmentLoading"
      :initialDepartment="initialDepartment"
      :initialScope="initialScope"
      @onChatterClick="handleChatterClick"
      @onSearch="handleSearch"
      @onFilterChange="handleFilterChange"
      @onRemoveFilter="handleRemoveFilter"
      @onClearFilters="handleClearFilters"
      @onExcludeChange="handleExcludeChange"
      @onPaginationChange="handlePaginationChange"
      @onEmployeeClick="handleEmployeeClick"
      @onImportanceChange="handleImportanceChange"
      @onPositionsChatterClick="handlePositionsChatterClick"
      @onPositionsSearch="handlePositionsSearch"
      @onPositionsFilterChange="handlePositionsFilterChange"
      @onPositionsRemoveFilter="handlePositionsRemoveFilter"
      @onPositionsClearFilters="handlePositionsClearFilters"
      @onPositionsExcludeChange="handlePositionsExcludeChange"
      @onPositionsPaginationChange="handlePositionsPaginationChange"
      @onPositionClick="handlePositionClick"
      @onDepartmentSearch="handleDepartmentSearch"
      @onDepartmentClear="handleDepartmentClear"
    />
    <ChecklistDrawer
      :isEmployeeChatter="true"
      ref="drawerContainer"
      :drawer="isDrawerOpen"
      :avatar="{
        picture: currentEmployee?.avatar,
        firstName: currentEmployee?.first_name || '',
        lastName: currentEmployee?.last_name || '',
      }"
      :typeValue="(currentEmployee?.first_name || '') + ' ' + (currentEmployee?.last_name || '')"
      @closeDrawer="closeEmployeeChatterModal"
      :type="'Employee'"
      object-type="employee"
      :object-id="currentEmployee?.id ?? null"
      :chatterList="chatterData"
      :totalCheckpointChatter="totalEmployeeChatter"
      @requestMoreCheckpointsChatter="requestMoreChatter"
    />
    <ChecklistDrawer
      ref="positionDrawerContainer"
      :type="'Position'"
      :drawer="isPositionDrawerOpen"
      :avatar="{
        picture: currentPosition?.avatar || '',
        firstName: '',
        lastName: '',
      }"
      :typeValue="currentPosition?.name || ''"
      object-type="position"
      :object-id="currentPosition?.id ?? null"
      @closeDrawer="closePositionChatterModal"
      :chatterList="positionChatterData"
      :totalCheckpointChatter="totalPositionChatter"
      iconName="org_icon_position"
      @requestMoreCheckpointsChatter="requestMorePositionChatter"
    />
  </div>
</template>

<script>
import { onMounted, onBeforeUnmount, computed, getCurrentInstance, ref } from "vue";
import Employee from "@components/assets/icons/Employee.svg";
import Position from "@components/assets/icons/Position.svg";
import ReadinessPage from "@components/ReadinessPage/ReadinessPage.vue";
import ChecklistDrawer from "@components/Drawer/ChatterDrawer.vue";
import { useReadinessEmployees } from "@/composables/readinessEmployees/useReadinessEmployees";
import { useEmployees } from "@/composables/employees/useEmployees";
import { usePositions } from "@/composables/positions/usePositions";
import { transformChatterData } from "@/utils/chatterUtils";
import { LD_FLAG_IDEA_MDP_552 } from "@components/constants/launchDarklyFlagKeys";
import {
  getChatterList,
  recalculateEmployeeReadiness,
  getPositionsReadiness,
  getListOfDepartments,
} from "@/api";
import { useFilters } from "@/composables/filters/useFilters";
import {
  getCleanedFilters,
  applyLocalImportanceState,
  resolveAssignmentId,
  computeAccountReadiness,
  computeFallbackAverageReadiness,
  resolveAverageReadinessAfterRecalc,
  applyRecalcResponseToEmployee,
  shouldExitRecalcPoll,
} from "@/views/NewReadiness/NewReadinessHelpers";

export default {
  components: {
    ReadinessPage,
    ChecklistDrawer,
  },
  setup() {
    const app = getCurrentInstance();
    const { proxy } = app;
    const employees = useReadinessEmployees();
    const employeeService = useEmployees();
    const positionService = usePositions();
    const filter = useFilters();

    // Load saved filters from localStorage
    const savedFilters = filter.getFilters("readinessEmployees");

    // Clean up saved filters: remove "no_filter" if "requires_recalc" is present
    const cleanedSavedFilters = getCleanedFilters(savedFilters);

    const positionsEnabled = ref(false);

    const baseMenuItems = [
      {
        label: "Employees",
        icon: Employee,
      },
      {
        label: "Positions",
        icon: Position,
      },
    ];

    const averageReadiness = ref(0);
    const last_month_averageReadiness = ref(null);

    const initialTab = ref("Employees");
    const currentEmployee = ref(null);
    const isDrawerOpen = ref(false);
    const chatterData = ref([]);
    const totalEmployeeChatter = ref(0);
    const chatterPage = ref(1);
    const searchText = ref("");
    const appliedFilters = ref(cleanedSavedFilters);
    const readinessLoading = ref(false);
    // Starts true so the Employees table shows its skeleton from the first render, not an empty state.
    const employeesLoading = ref(true);
    const recalculating = ref(false);
    const filterGroups = ref([
      {
        label: "Recalculation",
        items: [
          { label: "No Filter", value: "no_filter" },
          { label: "Requires Recalc", value: "requires_recalc" },
        ],
      },
    ]);

    // --- Positions readiness tab state ---
    const positions = ref([]);
    const positionsTotalCount = ref(0);
    const positionsCurrentPage = ref(1);
    const positionsItemsPerPage = ref(10);
    const positionsLoading = ref(false);
    const positionsSearchText = ref("");
    const positionsAppliedFilters = ref([]);
    const positionsAverageReadiness = ref(0);
    const departments = ref([]);
    const departmentLoading = ref(false);
    const initialDepartment = ref(null);
    const initialScope = ref("subtree");
    const positionsFilterGroups = ref([
      {
        label: "Vacancy",
        items: [
          { label: "No Filter", value: "no_filter" },
          { label: "Vacant", value: "vacant" },
          { label: "Not Vacant", value: "not_vacant" },
        ],
      },
      {
        label: "Excluded from Average",
        items: [
          { label: "No Filter", value: "no_filter" },
          { label: "Excluded", value: "excluded" },
          { label: "Not Excluded", value: "not_excluded" },
        ],
      },
    ]);
    const currentPosition = ref(null);
    const isPositionDrawerOpen = ref(false);
    const positionChatterData = ref([]);
    const totalPositionChatter = ref(0);
    const positionChatterPage = ref(1);
    // -------------------------------

    const totalCount = computed(() => {
      return employees.employeeStore.getTotalEmployeesCount || 0;
    });

    const currentPage = computed(() => {
      return employees.employeeStore.getPagination.currentPage || 1;
    });

    const itemsPerPage = computed(() => {
      return employees.employeeStore.getPagination.itemsPerPage || 10;
    });

    const resetPagination = () => {
      if (employees.employeeStore) {
        employees.employeeStore.setPagination({
          currentPage: 1,
          itemsPerPage: 10,
          totalCount: employees.employeeStore.getTotalEmployeesCount,
        });
      }
    };

    // silent: refetch after a user edit (exclude / importance) must not rebuild the table.
    const loadEmployeesWithFilters = async ({ silent = false } = {}) => {
      // Update the employee store's pagination state to match our viewState BEFORE making API call
      const employeeStore = employees.employeeStore;
      if (employeeStore) {
        employeeStore.setPagination({
          currentPage: employeeStore.getPagination.currentPage || 1,
          itemsPerPage: employeeStore.getPagination.itemsPerPage || 10,
          totalCount: employeeStore.getTotalEmployeesCount,
        });
      }

      const queryParams = {
        limit: employeeStore?.getPagination?.itemsPerPage || 10,
        offset:
          ((employeeStore?.getPagination?.currentPage || 1) - 1) *
          (employeeStore?.getPagination?.itemsPerPage || 10),
        ordering: "first_name",
        status: "active",
        archived: false,
      };

      // Add search parameter
      if (searchText.value && searchText.value.trim()) {
        queryParams.name = searchText.value.trim();
      }

      // Add filter parameters
      // Apply needs_recalc filter if "requires_recalc" is in filters
      // Note: "no_filter" should already be excluded in handleFilterChange, but double-check here
      const effectiveFilters = appliedFilters.value.filter((item) => item !== "no_filter");
      if (effectiveFilters.includes("requires_recalc")) {
        queryParams.needs_recalc = true;
      }

      if (!silent) employeesLoading.value = true;
      try {
        await employees.loadAllEmployees(queryParams);
      } finally {
        if (!silent) employeesLoading.value = false;
      }
    };

    // --- Positions readiness tab: list, filters, pagination ---
    const resetPositionsPagination = () => {
      positionsCurrentPage.value = 1;
      positionsItemsPerPage.value = 10;
    };

    const resolvePositionsQueryParams = () => {
      const queryParams = {
        limit: positionsItemsPerPage.value || 10,
        offset: ((positionsCurrentPage.value || 1) - 1) * (positionsItemsPerPage.value || 10),
        ordering: "name",
        status: "active",
        archived: false,
      };

      if (positionsSearchText.value && positionsSearchText.value.trim()) {
        queryParams.name = positionsSearchText.value.trim();
      }

      const effectiveFilters = (positionsAppliedFilters.value || []).filter(
        (item) => item !== "no_filter"
      );
      if (effectiveFilters.includes("vacant")) {
        queryParams.vacancy = "VACANT";
      } else if (effectiveFilters.includes("not_vacant")) {
        queryParams.vacancy = "NOT_VACANT";
      }
      if (effectiveFilters.includes("excluded")) {
        queryParams.exclude_from_avg = "EXCLUDED";
      } else if (effectiveFilters.includes("not_excluded")) {
        queryParams.exclude_from_avg = "NOT_EXCLUDED";
      }

      const departmentFilter = (positionsAppliedFilters.value || []).find(
        (item) => item && typeof item === "object" && item.id
      );
      if (departmentFilter) {
        queryParams.department_id = departmentFilter.id;
        queryParams.department_scope = departmentFilter.scope === "direct" ? "direct" : "subtree";
      }

      return queryParams;
    };

    const applyPositionsAccountDetails = (data) => {
      if (!data) return;
      if (data.avg_position_readiness !== null && data.avg_position_readiness !== undefined) {
        positionsAverageReadiness.value = data.avg_position_readiness;
      }
    };

    const loadPositionsWithFilters = async () => {
      positionsLoading.value = true;
      try {
        const accountID = JSON.parse(localStorage.getItem("currentAccountID"));
        const response = await getPositionsReadiness({
          account_id: accountID,
          queryParamsObj: resolvePositionsQueryParams(),
        });
        const data = response?.data || {};
        positions.value = data.results || data.positions || data || [];
        positionsTotalCount.value = data.count ?? positions.value.length;
      } catch (e) {
        console.error("Error loading positions readiness: ", e);
        positions.value = [];
        positionsTotalCount.value = 0;
      } finally {
        positionsLoading.value = false;
      }
    };

    const loadPositionsAccountDetails = async () => {
      try {
        const accountID = JSON.parse(localStorage.getItem("currentAccountID"));
        const response = await employeeService.getAccountDetails(accountID);
        applyPositionsAccountDetails(response?.data);
      } catch (e) {
        console.error("Error loading position readiness average: ", e);
      }
    };

    const loadDepartments = async (search) => {
      departmentLoading.value = true;
      try {
        const accountID = JSON.parse(localStorage.getItem("currentAccountID"));
        const response = await getListOfDepartments(
          accountID,
          search || "",
          { status: "active", archived: false },
          { limit: 20, offset: 0 }
        );
        departments.value = response?.data?.results || [];
      } catch (e) {
        console.error("Error loading departments: ", e);
        departments.value = [];
      } finally {
        departmentLoading.value = false;
      }
    };

    // --- Positions chatter ---
    const getPositionChatter = async () => {
      try {
        if (!currentPosition.value?.id) return;
        const accountID = JSON.parse(localStorage.getItem("currentAccountID"));
        const response = await positionService.positionChatter(
          accountID,
          currentPosition.value.id,
          "position",
          positionChatterPage.value
        );
        if (response?.data) {
          const chatterItems = transformChatterData(response.data.results);
          positionChatterData.value.push(...chatterItems);
          totalPositionChatter.value = response.data.count;
        }
      } catch (error) {
        console.error("Error loading position chatter: ", error);
      }
    };

    const handlePositionsChatterClick = async (positionData) => {
      try {
        currentPosition.value = positionData;
        positionService.setCurrentPosition(positionData);
        positionChatterData.value = [];
        positionChatterPage.value = 1;
        isPositionDrawerOpen.value = true;
        await getPositionChatter();
      } catch (error) {
        console.error("Error opening position chatter modal: ", error);
      }
    };

    const closePositionChatterModal = () => {
      positionChatterData.value = [];
      totalPositionChatter.value = 0;
      positionChatterPage.value = 1;
      isPositionDrawerOpen.value = false;
      currentPosition.value = null;
    };

    const requestMorePositionChatter = async () => {
      positionChatterPage.value = Math.ceil(positionChatterData.value.length / 10) + 1;
      await getPositionChatter();
    };

    // --- Positions handlers ---
    const handlePositionsFilterChange = async (filters) => {
      const vacancy = filters?.vacancy || null;
      const excluded = filters?.excluded || null;
      const department = filters?.department || null;
      const scope = filters?.scope || "subtree";

      const nextFilters = [];
      if (vacancy && vacancy !== "no_filter") nextFilters.push(vacancy);
      if (excluded && excluded !== "no_filter") nextFilters.push(excluded);
      if (department?.id) {
        initialDepartment.value = { ...department, scope };
        initialScope.value = scope;
        nextFilters.push({ ...department, scope });
      } else {
        initialDepartment.value = null;
        initialScope.value = "subtree";
      }

      positionsAppliedFilters.value = nextFilters;
      resetPositionsPagination();
      await loadPositionsWithFilters();
      await loadPositionsAccountDetails();
    };

    const handlePositionsRemoveFilter = async (removedFilter) => {
      const labelToValueMap = {};
      positionsFilterGroups.value.forEach((group) => {
        group.items.forEach((item) => {
          labelToValueMap[item.label] = item.value;
        });
      });

      const valueToRemove = labelToValueMap[removedFilter] || removedFilter;
      const updatedFilters = positionsAppliedFilters.value.filter((item) => {
        if (item && typeof item === "object") {
          return item.name !== removedFilter;
        }
        return item !== valueToRemove;
      });

      if (valueToRemove === removedFilter && initialDepartment.value?.name === removedFilter) {
        // Department chip removed
        initialDepartment.value = null;
        initialScope.value = "subtree";
      }

      positionsAppliedFilters.value = updatedFilters;
      resetPositionsPagination();
      await loadPositionsWithFilters();
      await loadPositionsAccountDetails();
    };

    const handlePositionsClearFilters = async () => {
      positionsAppliedFilters.value = [];
      positionsSearchText.value = "";
      initialDepartment.value = null;
      initialScope.value = "subtree";
      resetPositionsPagination();
      await loadPositionsWithFilters();
      await loadPositionsAccountDetails();
    };

    const handlePositionsExcludeChange = async ({ position, value }) => {
      if (!position || !position.id) {
        console.error("Invalid position data in handlePositionsExcludeChange");
        return;
      }
      try {
        proxy.$loader.show();
        positionsLoading.value = true;
        const accountID = JSON.parse(localStorage.getItem("currentAccountID"));
        await positionService.updatePositionExcludeFromAvg(accountID, {
          position_id: position.id,
          value: value.value,
        });
        await loadPositionsWithFilters();
        await loadPositionsAccountDetails();
      } catch (error) {
        console.error("Error updating position exclude from average: ", error);
        positionsLoading.value = false;
      } finally {
        proxy.$loader.hide();
      }
    };

    const handlePositionsPaginationChange = async (paginationData) => {
      try {
        proxy.$loader.show();
        positionsCurrentPage.value = paginationData.page;
        positionsItemsPerPage.value = paginationData.itemsPerPage || positionsItemsPerPage.value;
        await loadPositionsWithFilters();
        await loadPositionsAccountDetails();
      } catch (error) {
        console.error("Error handling positions pagination change: ", error);
      } finally {
        proxy.$loader.hide();
      }
    };

    const handlePositionClick = (data) => {
      if (data && data.id) {
        proxy.$router
          .push({
            name: "NewOrganization",
            params: { tab: "positions" },
            query: {
              positionId: String(data.id),
              fromReadiness: "1",
            },
          })
          .catch((err) => {
            if (err.name !== "NavigationDuplicated") {
              console.error("Error navigating to position detail: ", err);
            }
          });
      }
    };

    const handleDepartmentSearch = (query) => {
      loadDepartments(query || "");
    };

    const handleDepartmentClear = () => {
      loadDepartments("");
    };
    // -------------------------------

    // Apply account-details payload to local refs. Kept separate from the
    // fetcher so the recalc poll below can reuse the same mapping.
    const applyAccountDetails = (d, { baselineAvg } = {}) => {
      if (!d) return;
      const employeeList = employees.employeeStore.employees;
      const result =
        baselineAvg === undefined
          ? computeAccountReadiness(d)
          : resolveAverageReadinessAfterRecalc(d, employeeList, baselineAvg);
      averageReadiness.value = result.averageReadiness;
      last_month_averageReadiness.value = result.lastMonthReadiness;
    };

    const loadAccountDetails = async ({ baselineAvg } = {}) => {
      readinessLoading.value = true;
      try {
        const accountID = JSON.parse(localStorage.getItem("currentAccountID"));
        const response = await employeeService.getAccountDetails(accountID);
        const data = response?.data;
        applyAccountDetails(data, { baselineAvg });
        // Initial load only: when API omits avg_employee_readiness, estimate from page.
        const hasAvgEmployeeReadiness =
          data?.avg_employee_readiness !== null && data?.avg_employee_readiness !== undefined;
        if (baselineAvg === undefined && !hasAvgEmployeeReadiness && averageReadiness.value === 0) {
          const fallback = computeFallbackAverageReadiness(employees.employeeStore.employees);
          if (fallback) averageReadiness.value = fallback;
        }
        return data || null;
      } catch (e) {
        console.error("Error loading account details: ", e);
        return null;
      } finally {
        readinessLoading.value = false;
      }
    };

    // Poll account details until the aggregate moves or enough time passes.
    // readiness_recalc_in_progress is often false immediately, so do not exit
    // on the first poll when baselineAvg is provided (importance change flow).
    let activeRecalcPoll = null;
    const waitForRecalcCompletion = async ({
      baselineAvg,
      intervalMs = 1000,
      timeoutMs = 30000,
      minWaitMs = 1500,
      settleWaitMs = 3000,
    } = {}) => {
      const accountID = JSON.parse(localStorage.getItem("currentAccountID"));
      const token = { cancelled: false };
      activeRecalcPoll = token;
      const start = Date.now();
      let sawRecalcInProgress = false;
      try {
        while (!token.cancelled && Date.now() - start < timeoutMs) {
          let data = null;
          try {
            const response = await employeeService.getAccountDetails(accountID);
            data = response?.data || null;
            applyAccountDetails(data, { baselineAvg });
          } catch (e) {
            console.error("Error polling recalc state:", e);
          }

          const elapsed = Date.now() - start;
          const currentAvg = averageReadiness.value;

          if (data?.readiness_recalc_in_progress) {
            sawRecalcInProgress = true;
          }

          if (
            shouldExitRecalcPoll({
              baselineAvg,
              currentAvg,
              elapsedMs: elapsed,
              sawRecalcInProgress,
              readinessRecalcInProgress: data?.readiness_recalc_in_progress,
              hasAccountData: Boolean(data),
              minWaitMs,
              settleWaitMs,
            })
          ) {
            return data;
          }

          await new Promise((resolve) => setTimeout(resolve, intervalMs));
        }
        return null;
      } finally {
        if (activeRecalcPoll === token) {
          activeRecalcPoll = null;
        }
      }
    };

    onBeforeUnmount(() => {
      if (activeRecalcPoll) {
        activeRecalcPoll.cancelled = true;
      }
    });

    onMounted(async () => {
      try {
        proxy.$loader.show();
        // Initialize pagination state
        resetPagination();
        resetPositionsPagination();
        positionsEnabled.value = (await proxy.$getFlag(LD_FLAG_IDEA_MDP_552, false)) === true;
        await loadEmployeesWithFilters();
        await loadAccountDetails();
        if (positionsEnabled.value) {
          await Promise.all([
            loadPositionsWithFilters(),
            loadPositionsAccountDetails(),
            loadDepartments(""),
          ]);
        }
      } catch (e) {
        console.error("Error loading data: ", e);
      } finally {
        proxy.$loader.hide();
      }
    });

    const activeEmployeesCount = computed(() => {
      return employees.employeeStore.getTotalEmployeesCount || 0;
    });

    const menuItems = computed(() => {
      return (baseMenuItems || [])
        .filter((item) => item?.label !== "Positions" || positionsEnabled.value)
        .map((item) => {
          if (item?.label === "Employees") {
            return { ...item, badgeCount: activeEmployeesCount.value };
          }
          return item;
        });
    });

    const selectTab = (payload) => {
      const tab = typeof payload === "string" ? payload : payload?.label;
      if (!tab) return;
      initialTab.value = tab;
    };

    const getEmployeeChatter = async () => {
      try {
        if (!currentEmployee.value?.id) return;

        const accountID = JSON.parse(localStorage.getItem("currentAccountID"));
        const response = await getChatterList(
          accountID,
          currentEmployee.value.id,
          "employee",
          chatterPage.value
        );

        if (response?.data) {
          const chatterItems = transformChatterData(response.data.results);
          chatterData.value.push(...chatterItems);
          totalEmployeeChatter.value = response.data.count;
        }
      } catch (error) {
        console.error("Error loading employee chatter: ", error);
      }
    };

    const handleChatterClick = async (employeeData) => {
      try {
        // Set current employee
        currentEmployee.value = employeeData;
        employeeService.setCurrentEmployee(employeeData);

        // Reset chatter state
        chatterData.value = [];
        chatterPage.value = 1;
        isDrawerOpen.value = true;

        // Load chatter data
        await getEmployeeChatter();
      } catch (error) {
        console.error("Error opening chatter modal: ", error);
      }
    };

    const closeEmployeeChatterModal = () => {
      chatterData.value = [];
      totalEmployeeChatter.value = 0;
      chatterPage.value = 1;
      isDrawerOpen.value = false;
      currentEmployee.value = null;
    };

    const requestMoreChatter = async () => {
      chatterPage.value = Math.ceil(chatterData.value.length / 10) + 1;
      await getEmployeeChatter();
    };

    // Debounce function for search
    const debounce = (func, delay) => {
      let timeout;
      return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => func(...args), delay);
      };
    };

    // Perform search with proper filtering
    const performSearch = async (searchTextValue) => {
      // Clear previous search
      if (searchText.value) {
        searchText.value = "";
      }

      // Only add search filters and load employees if there's actually a search term
      if (searchTextValue && searchTextValue.trim() !== "") {
        searchText.value = searchTextValue.trim();
      }

      // Reset to first page when searching
      resetPagination();

      // Load employees with current filters (including search), then refresh
      // the account-level average so the banner stays consistent
      await loadEmployeesWithFilters();
      await loadAccountDetails();
    };

    // Debounced search function
    const debouncedSearch = debounce(performSearch, 500);

    // Handle search input
    const handleSearch = (text) => {
      debouncedSearch(text);
    };

    // Handle positions search input (same debounce cadence as employees)
    const performPositionsSearch = async (searchTextValue) => {
      if (positionsSearchText.value) {
        positionsSearchText.value = "";
      }
      if (searchTextValue && searchTextValue.trim() !== "") {
        positionsSearchText.value = searchTextValue.trim();
      }
      resetPositionsPagination();
      await loadPositionsWithFilters();
      await loadPositionsAccountDetails();
    };

    const debouncedPositionsSearch = debounce(performPositionsSearch, 500);

    const handlePositionsSearch = (text) => {
      debouncedPositionsSearch(text);
    };

    // Handle filter change
    const handleFilterChange = async (filters) => {
      // Create maps for label/value conversion
      const labelToValueMap = {};
      const valueToLabelMap = {};
      filterGroups.value.forEach((group) => {
        group.items.forEach((item) => {
          labelToValueMap[item.label] = item.value;
          valueToLabelMap[item.value] = item.label;
        });
      });

      const newArray = filters
        .map((item) => {
          // Handle string values (could be label or value)
          if (typeof item === "string") {
            // If it's a value (like "requires_recalc"), return it
            if (valueToLabelMap[item]) {
              return item;
            }
            // If it's a label (like "Requires Recalc"), map to value
            return labelToValueMap[item] || item;
          }
          // Handle object values from RadioInput
          if (item && typeof item === "object") {
            // If value is null or undefined, skip it (e.g., "No Filter" deselected)
            if (item.value === null || item.value === undefined) {
              return null;
            }
            // RadioInput emits labels, so map label to value
            if (Object.hasOwn(item, "value")) {
              const filterValue = labelToValueMap[item.value] || item.value;
              return filterValue;
            }
          }
          return null;
        })
        .filter(Boolean);

      // Remove "no_filter" if "requires_recalc" is selected (they are mutually exclusive)
      const filteredArray = newArray.includes("requires_recalc")
        ? newArray.filter((item) => item !== "no_filter")
        : newArray;

      filter.setFilters({ key: "readinessEmployees", filters: filteredArray });
      appliedFilters.value = filteredArray;

      // Reset pagination when filters are applied
      resetPagination();
      await loadEmployeesWithFilters();
      await loadAccountDetails();
    };

    // Handle remove filter
    const handleRemoveFilter = async (removedFilter) => {
      // Create maps for label/value conversion
      const labelToValueMap = {};
      const valueToLabelMap = {};
      filterGroups.value.forEach((group) => {
        group.items.forEach((item) => {
          labelToValueMap[item.label] = item.value;
          valueToLabelMap[item.value] = item.label;
        });
      });

      // Convert removedFilter (which is a label from chip) to value
      const filterValueToRemove = labelToValueMap[removedFilter] || removedFilter;

      // Remove the filter from appliedFilters
      const updatedFilters = appliedFilters.value.filter(
        (filterValue) => filterValue !== filterValueToRemove
      );

      appliedFilters.value = updatedFilters;
      filter.setFilters({
        key: "readinessEmployees",
        filters: updatedFilters,
      });

      // Reset pagination and reload employees
      resetPagination();
      await loadEmployeesWithFilters();
      await loadAccountDetails();
    };

    // Handle clear filters
    const handleClearFilters = async () => {
      appliedFilters.value = [];
      searchText.value = "";
      filter.setFilters({ key: "readinessEmployees", filters: [] });
      resetPagination();
      await loadEmployeesWithFilters();
      await loadAccountDetails();
    };

    // Handle exclude from average change
    const handleExcludeChange = async ({ employee, value }) => {
      if (!employee || !employee.id) {
        console.error("Invalid employee data in handleExcludeChange");
        return;
      }
      try {
        proxy.$loader.show();
        readinessLoading.value = true;

        // Get the actual current page from the store to preserve pagination
        const employeeStore = employees.employeeStore;
        const actualCurrentPage = employeeStore?.getPagination?.currentPage || 1;
        const actualItemsPerPage = employeeStore?.getPagination?.itemsPerPage || 10;

        // Update pagination state to match store before making API call
        if (employeeStore) {
          employeeStore.setPagination({
            currentPage: actualCurrentPage,
            itemsPerPage: actualItemsPerPage,
            totalCount: employeeStore.getTotalEmployeesCount,
          });
        }

        const accountID = JSON.parse(localStorage.getItem("currentAccountID"));
        const payload = {
          emp_id: employee.id,
          value: value.value,
        };
        await employeeService.updateEmployeeExcludeFromAvg(accountID, payload);

        // Update pagination state again before loading to preserve current page
        if (employeeStore) {
          employeeStore.setPagination({
            currentPage: actualCurrentPage,
            itemsPerPage: actualItemsPerPage,
            totalCount: employeeStore.getTotalEmployeesCount,
          });
        }

        // Reload employees with current filters to preserve query parameters
        await loadEmployeesWithFilters({ silent: true });
        // Load account details to update average readiness
        await loadAccountDetails();
      } catch (error) {
        console.error("Error updating exclude from average: ", error);
        readinessLoading.value = false;
      } finally {
        proxy.$loader.hide();
      }
    };

    // Handle importance change
    const handleImportanceChange = async ({ employee, newValue, kind }) => {
      const baselineAvg = averageReadiness.value;
      try {
        recalculating.value = true;

        if (employee) {
          applyLocalImportanceState(employee, kind, newValue);
        }

        const accountID = JSON.parse(localStorage.getItem("currentAccountID"));
        const assignmentId = resolveAssignmentId(employee, kind);

        if (!assignmentId) {
          console.error("Assignment ID not found for", kind);
          recalculating.value = false;
          return;
        }

        const payload = {
          assignment_type: "position_assignment",
          assignment_id: assignmentId,
          new_importance: newValue,
        };

        await employeeService.updateImportanceValue(accountID, payload);

        const recalcResponse = await recalculateEmployeeReadiness(accountID, employee.id);
        applyRecalcResponseToEmployee(employee, recalcResponse?.data);

        // Refresh employees + account aggregate immediately so the banner
        // and rows reflect whatever the backend has so far. The animated
        // bars (header + per-row) tween smoothly to the new value.
        await Promise.all([
          loadEmployeesWithFilters({ silent: true }),
          loadAccountDetails({ baselineAvg }),
        ]);

        // Poll until avg moves or enough time passes (legacy screen used ~1.5s delay).
        try {
          await waitForRecalcCompletion({ baselineAvg });
          await loadEmployeesWithFilters({ silent: true });
          await loadAccountDetails({ baselineAvg });
        } finally {
          recalculating.value = false;
        }
      } catch (error) {
        console.error("Error changing importance:", error);
        recalculating.value = false;
      }
    };

    // Handle pagination change
    const handlePaginationChange = async (paginationData) => {
      try {
        proxy.$loader.show();
        // Update pagination state in store
        if (employees.employeeStore) {
          employees.employeeStore.setPagination({
            currentPage: paginationData.page,
            itemsPerPage: paginationData.itemsPerPage || itemsPerPage.value,
            totalCount: totalCount.value,
          });
        }
        // Load employees with current filters to preserve query parameters,
        // then refresh the account-level average so the banner stays
        // consistent across pages (it's an account aggregate, not a page subset)
        await loadEmployeesWithFilters();
        await loadAccountDetails();
      } catch (error) {
        console.error("Error handling pagination change: ", error);
      } finally {
        proxy.$loader.hide();
      }
    };

    // Handle employee click - navigate to EmployeeDetail (with fromReadiness for breadcrumbs)
    const handleEmployeeClick = (data) => {
      if (data && data.employeeId) {
        // Navigate to new organization employees tab with employee detail
        proxy.$router
          .push({
            name: "NewOrganization",
            params: { tab: "employees" },
            query: {
              employeeId: String(data.employeeId),
              employeeTab: data.tab || "pathtoreadiness",
              fromReadiness: "1",
            },
          })
          .catch((err) => {
            if (err.name !== "NavigationDuplicated") {
              console.error("Error navigating to employee detail: ", err);
            }
          });
      }
    };

    // Convert filter values to labels for SearchModal (it expects labels in defaultSelected)
    const appliedFiltersAsLabels = computed(() => {
      const valueToLabelMap = {};
      filterGroups.value.forEach((group) => {
        group.items.forEach((item) => {
          valueToLabelMap[item.value] = item.label;
        });
      });
      return (appliedFilters.value || []).map((value) => valueToLabelMap[value] || value);
    });

    // Filter employees on frontend based on applied filters
    const filteredEmployees = computed(() => {
      let allEmployees = employees.allEmployees.value || [];

      // Apply needs_recalc filter on frontend if "requires_recalc" is in filters
      const effectiveFilters = (appliedFilters.value || []).filter(
        (item) => item !== "no_filter" && item !== null && item !== undefined
      );

      if (effectiveFilters.includes("requires_recalc")) {
        allEmployees = allEmployees.filter((employee) => employee.needs_recalc === true);
      }

      return allEmployees;
    });

    return {
      menuItems,
      averageReadiness,
      last_month_averageReadiness,
      employees: filteredEmployees,
      initialTab,
      selectTab,
      handleChatterClick,
      closeEmployeeChatterModal,
      requestMoreChatter,
      isDrawerOpen,
      currentEmployee,
      chatterData,
      totalEmployeeChatter,
      filterGroups,
      appliedFilters,
      appliedFiltersAsLabels,
      searchText,
      handleSearch,
      handleFilterChange,
      handleRemoveFilter,
      handleClearFilters,
      handleExcludeChange,
      handlePaginationChange,
      handleEmployeeClick,
      handleImportanceChange,
      totalCount,
      currentPage,
      itemsPerPage,
      readinessLoading,
      employeesLoading,
      recalculating,
      positions,
      positionsAverageReadiness,
      positionsFilterGroups,
      positionsAppliedFilters,
      positionsSearchText,
      positionsTotalCount,
      positionsCurrentPage,
      positionsItemsPerPage,
      positionsLoading,
      departments,
      departmentLoading,
      initialDepartment,
      initialScope,
      handlePositionsChatterClick,
      closePositionChatterModal,
      requestMorePositionChatter,
      isPositionDrawerOpen,
      currentPosition,
      positionChatterData,
      totalPositionChatter,
      handlePositionsSearch,
      handlePositionsFilterChange,
      handlePositionsRemoveFilter,
      handlePositionsClearFilters,
      handlePositionsExcludeChange,
      handlePositionsPaginationChange,
      handlePositionClick,
      handleDepartmentSearch,
      handleDepartmentClear,
    };
  },
};
</script>

<style lang="css" scoped>
.readiness-container-new-readiness {
  margin: 15px;
  width: 100%;
}
</style>
